import * as PIXI from 'pixi.js';
import { GRID_COLS, GRID_ROWS, CELL_SIZE, GEM_SIZE, GEM_COLORS, MERGE_ANIMATION_DURATION, CURSED_SHARD_COLOR } from './constants.js';

export class GameRenderer {
  constructor(canvas) {
    this.canvas = canvas;
    this.app = null;
    this.gridContainer = null;
    this.gemSprites = new Map();
    this.cursedShardGraphics = null;
    this.dragSprite = null;
    this.dragOffset = { x: 0, y: 0 };
    this.onMergeCallback = null;
  }
  
  async init() {
    this.app = new PIXI.Application({
      view: this.canvas,
      width: GRID_COLS * CELL_SIZE,
      height: GRID_ROWS * CELL_SIZE + 100, // Extra space for enemies
      backgroundColor: 0x1a1a2e,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true
    });
    
    // Create grid container
    this.gridContainer = new PIXI.Container();
    this.gridContainer.y = 100; // Offset for enemy area
    this.app.stage.addChild(this.gridContainer);
    
    // Draw grid background
    this.drawGridBackground();
    
    // Create cursed shard container (behind gems)
    this.cursedShardGraphics = new PIXI.Graphics();
    this.gridContainer.addChild(this.cursedShardGraphics);
    
    // Enable interaction
    this.gridContainer.eventMode = 'static';
    this.gridContainer.hitArea = new PIXI.Rectangle(
      0, 0,
      GRID_COLS * CELL_SIZE,
      GRID_ROWS * CELL_SIZE
    );
  }
  
  drawGridBackground() {
    const graphics = new PIXI.Graphics();
    
    for (let row = 0; row < GRID_ROWS; row++) {
      for (let col = 0; col < GRID_COLS; col++) {
        const x = col * CELL_SIZE;
        const y = row * CELL_SIZE;
        
        // Cell background
        graphics.beginFill(0x2a2a4a, 0.5);
        graphics.drawRoundedRect(x + 2, y + 2, CELL_SIZE - 4, CELL_SIZE - 4, 8);
        graphics.endFill();
        
        // Cell border
        graphics.lineStyle(1, 0x4a4a6a, 0.3);
        graphics.drawRoundedRect(x + 2, y + 2, CELL_SIZE - 4, CELL_SIZE - 4, 8);
      }
    }
    
    this.gridContainer.addChild(graphics);
  }
  
  createGemSprite(gem) {
    const container = new PIXI.Container();
    
    // Get color based on tier
    const colors = GEM_COLORS[gem.type];
    const color = colors[gem.tier - 1];
    
    // Draw gem shape
    const graphics = new PIXI.Graphics();
    
    // Glow effect
    graphics.beginFill(color, 0.3);
    graphics.drawCircle(GEM_SIZE / 2, GEM_SIZE / 2, GEM_SIZE / 2 + 5);
    graphics.endFill();
    
    // Main gem body
    graphics.beginFill(color);
    graphics.drawCircle(GEM_SIZE / 2, GEM_SIZE / 2, GEM_SIZE / 2);
    graphics.endFill();
    
    // Highlight
    graphics.beginFill(0xffffff, 0.4);
    graphics.drawCircle(GEM_SIZE / 2 - 10, GEM_SIZE / 2 - 10, GEM_SIZE / 4);
    graphics.endFill();
    
    // Tier indicator
    graphics.lineStyle(2, 0xffffff);
    const tierRadius = GEM_SIZE / 2 - 8;
    for (let i = 0; i < gem.tier; i++) {
      const angle = (i * Math.PI * 2) / gem.tier - Math.PI / 2;
      const x = GEM_SIZE / 2 + Math.cos(angle) * tierRadius;
      const y = GEM_SIZE / 2 + Math.sin(angle) * tierRadius;
      graphics.drawCircle(x, y, 3);
    }
    
    container.addChild(graphics);
    
    // Position in grid
    container.x = gem.col * CELL_SIZE + (CELL_SIZE - GEM_SIZE) / 2;
    container.y = gem.row * CELL_SIZE + (CELL_SIZE - GEM_SIZE) / 2;
    
    // Store gem data
    container.gemData = gem;
    
    return container;
  }
  
  updateGrid(grid, draggedGem = null, cursedShards = []) {
    // Clear existing sprites
    for (const [id, sprite] of this.gemSprites) {
      if (sprite.parent) {
        sprite.parent.removeChild(sprite);
      }
    }
    this.gemSprites.clear();
    
    // Render cursed shards
    this.renderCursedShards(cursedShards);
    
    // Create new sprites
    for (let row = 0; row < GRID_ROWS; row++) {
      for (let col = 0; col < GRID_COLS; col++) {
        const gem = grid[row][col];
        if (gem && (!draggedGem || gem.id !== draggedGem.id)) {
          const sprite = this.createGemSprite(gem);
          this.gridContainer.addChild(sprite);
          this.gemSprites.set(gem.id, sprite);
        }
      }
    }
    
    // Update drag sprite if exists
    if (draggedGem && this.dragSprite) {
      this.dragSprite.visible = true;
    } else if (this.dragSprite) {
      this.dragSprite.visible = false;
    }
  }
  
  // Render cursed shards on the grid
  renderCursedShards(shards) {
    this.cursedShardGraphics.clear();
    
    for (const shard of shards) {
      const x = shard.col * CELL_SIZE;
      const y = shard.row * CELL_SIZE;
      const centerX = x + CELL_SIZE / 2;
      const centerY = y + CELL_SIZE / 2;
      
      // Outer glow
      this.cursedShardGraphics.beginFill(CURSED_SHARD_COLOR, 0.2);
      this.cursedShardGraphics.drawCircle(centerX, centerY, CELL_SIZE / 2 - 2);
      this.cursedShardGraphics.endFill();
      
      // Main shard body (crystal shape)
      this.cursedShardGraphics.beginFill(CURSED_SHARD_COLOR, 0.8);
      this.cursedShardGraphics.moveTo(centerX, centerY - 18);
      this.cursedShardGraphics.lineTo(centerX + 14, centerY);
      this.cursedShardGraphics.lineTo(centerX, centerY + 18);
      this.cursedShardGraphics.lineTo(centerX - 14, centerY);
      this.cursedShardGraphics.closePath();
      this.cursedShardGraphics.endFill();
      
      // Inner highlight
      this.cursedShardGraphics.beginFill(0xffffff, 0.3);
      this.cursedShardGraphics.drawCircle(centerX - 4, centerY - 4, 5);
      this.cursedShardGraphics.endFill();
    }
  }
  
  createDragSprite(gem) {
    const sprite = this.createGemSprite(gem);
    sprite.alpha = 0.8;
    sprite.visible = false;
    this.gridContainer.addChild(sprite);
    this.dragSprite = sprite;
    return sprite;
  }
  
  updateDragPosition(globalX, globalY) {
    if (!this.dragSprite) return;
    
    const localPos = this.gridContainer.toLocal({ x: globalX, y: globalY });
    this.dragSprite.x = localPos.x - this.dragOffset.x;
    this.dragSprite.y = localPos.y - this.dragOffset.y;
  }
  
  getCellFromPosition(x, y) {
    const localPos = this.gridContainer.toLocal({ x, y });
    const col = Math.floor(localPos.x / CELL_SIZE);
    const row = Math.floor(localPos.y / CELL_SIZE);
    
    if (row >= 0 && row < GRID_ROWS && col >= 0 && col < GRID_COLS) {
      return { row, col };
    }
    return null;
  }
  
  // Get the center pixel coordinates of a cell (in local container coordinates)
  getCellCenter(row, col) {
    return {
      x: col * CELL_SIZE + CELL_SIZE / 2,
      y: row * CELL_SIZE + CELL_SIZE / 2
    };
  }
  
  // Convert local cell coordinates to global screen coordinates
  getCellGlobalCenter(row, col) {
    const local = this.getCellCenter(row, col);
    const global = this.gridContainer.toGlobal(local);
    return { x: global.x, y: global.y };
  }
  
  animateMerge(fromCell, toCell, onComplete) {
    const fromSprite = this.gemSprites.get(`gem-${fromCell.row}-${fromCell.col}`);
    
    if (!fromSprite) {
      if (onComplete) onComplete();
      return;
    }
    
    const targetX = toCell.col * CELL_SIZE + (CELL_SIZE - GEM_SIZE) / 2;
    const targetY = toCell.row * CELL_SIZE + (CELL_SIZE - GEM_SIZE) / 2;
    
    const startX = fromSprite.x;
    const startY = fromSprite.y;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / MERGE_ANIMATION_DURATION, 1);
      
      // Easing function
      const eased = 1 - Math.pow(1 - progress, 3);
      
      fromSprite.x = startX + (targetX - startX) * eased;
      fromSprite.y = startY + (targetY - startY) * eased;
      fromSprite.alpha = 1 - progress;
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        if (onComplete) onComplete();
      }
    };
    
    animate();
  }
  
  snapBackToOrigin(originalCell, onComplete) {
    if (!this.dragSprite) {
      if (onComplete) onComplete();
      return;
    }
    
    const targetX = originalCell.col * CELL_SIZE + (CELL_SIZE - GEM_SIZE) / 2;
    const targetY = originalCell.row * CELL_SIZE + (CELL_SIZE - GEM_SIZE) / 2;
    
    const startX = this.dragSprite.x;
    const startY = this.dragSprite.y;
    const startTime = Date.now();
    const duration = 200;
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const eased = 1 - Math.pow(1 - progress, 2);
      
      this.dragSprite.x = startX + (targetX - startX) * eased;
      this.dragSprite.y = startY + (targetY - startY) * eased;
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.dragSprite.visible = false;
        if (onComplete) onComplete();
      }
    };
    
    animate();
  }
  
  destroy() {
    if (this.app) {
      this.app.destroy(true);
      this.app = null;
    }
  }
}
